'use client'

import * as React from 'react'
import Link from 'next/link'
import { Search } from 'lucide-react'
import { cn } from '../../../../lib/utils'
import { SidebarMenu, SidebarMenuItem, SidebarMenuButton } from './index'

export type ThreadType = 'my-work' | 'teams' | 'conversations' | 'attention'

export interface ThreadItemData {
  id: string
  title: string | null
  type: ThreadType
  lastMessageAt: string | null
  href: string
  isActive?: boolean
  hasAttention?: boolean
}

export interface ThreadsListSectionProps {
  threads: ThreadItemData[]
  selectedThreadId?: string | null
  maxVisible?: number
  onLoadMore?: () => void
  hasMore?: boolean
  searchPlaceholder?: string
}

function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffWeeks = Math.floor(diffDays / 7)

  if (diffMins < 1) return 'now'
  if (diffMins < 60) return `${diffMins}m`
  if (diffHours < 24) return `${diffHours}h`
  if (diffDays < 7) return `${diffDays}d`
  if (diffWeeks < 4) return `${diffWeeks}w`

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function truncateTitle(title: string | null, maxLength: number = 22): string {
  if (!title) return 'New Thread'
  if (title.length <= maxLength) return title
  return title.slice(0, maxLength) + '...'
}

const ThreadItem = ({ thread }: { thread: ThreadItemData }) => {
  const relativeTime = formatRelativeTime(thread.lastMessageAt)

  return (
    <SidebarMenuItem className="-mb-0.5">
      <SidebarMenuButton
        asChild
        size="sm"
        className={cn(
          'flex justify-between hover:bg-accent/50 group/thread-item transition-[background-color,color] duration-200 ease-out',
          {
            'bg-accent/30': thread.isActive,
          },
        )}
        data-active={thread.isActive}
      >
        <Link href={thread.href} className="w-full">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="w-4 flex items-center justify-center shrink-0">
              <div
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-colors duration-200 ease-out',
                  thread.isActive
                    ? 'bg-message-background'
                    : 'bg-muted-foreground/40',
                )}
              />
            </div>
            <span
              className={cn(
                'truncate text-xs transition-colors duration-200 ease-out',
                thread.isActive
                  ? 'text-message-background font-medium'
                  : 'text-muted-foreground/60',
              )}
            >
              {truncateTitle(thread.title)}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {thread.hasAttention && (
              <div className="h-1.5 w-1.5 rounded-full bg-orange-500" />
            )}
            <span className="text-[10px] text-muted-foreground/50 tabular-nums">
              {relativeTime}
            </span>
          </div>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

const ThreadsListSection = ({
  threads,
  selectedThreadId,
  maxVisible = 10,
  onLoadMore,
  hasMore = false,
  searchPlaceholder = 'Search threads...',
}: ThreadsListSectionProps): React.JSX.Element => {
  const visibleThreads = threads.slice(0, maxVisible)
  const showMoreButton = hasMore || threads.length > maxVisible

  return (
    <div className="flex flex-col gap-2">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
        <input
          type="text"
          placeholder={searchPlaceholder}
          className={cn(
            'w-full h-7 pl-8 pr-3 text-xs rounded-md',
            'bg-accent/30 border-0',
            'text-muted-foreground placeholder:text-muted-foreground/50',
            'focus:outline-none focus:ring-1 focus:ring-ring/50',
            'transition-colors',
          )}
          readOnly
        />
      </div>

      {/* Thread List */}
      <SidebarMenu className="gap-0.5">
        {visibleThreads.map((thread) => (
          <ThreadItem
            key={thread.id}
            thread={{
              ...thread,
              isActive: thread.id === selectedThreadId,
            }}
          />
        ))}
      </SidebarMenu>

      {/* Load More Button */}
      {showMoreButton && (
        <button
          onClick={onLoadMore}
          className={cn(
            'text-xs text-muted-foreground/60 hover:text-muted-foreground',
            'py-1 px-2 rounded-md hover:bg-accent/30',
            'transition-colors text-left',
          )}
        >
          ... more
        </button>
      )}

      {/* Empty State */}
      {threads.length === 0 && (
        <div className="text-xs text-muted-foreground/50 py-2 px-2">
          No threads yet
        </div>
      )}
    </div>
  )
}

export { ThreadsListSection, formatRelativeTime, truncateTitle }
